import { useState, useEffect } from 'react'
import { authService } from '@/services/authService'

interface License {
  status: string
  expiresAt: string | null
  daysRemaining: number | null
  isBlocked: boolean
  subscriptionStatus: string
  hasStripeAccount: boolean
  planPreference: string | null
}

interface Agent {
  id: string
  name: string
  email: string
  subdomain: string
  phone?: string
  bio?: string
  specialties: string[]
  zones: string[]
  plan: string
  avatar_url?: string
  agent_type?: string
  parent_agent_id?: string
  license?: License
  sellers_count?: number
  nom_candidate?: boolean
}

export const useAuth = () => {
  const [agent, setAgent] = useState<Agent | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Detectar oauth_token en URL (callback de OAuth)
    const urlParams = new URLSearchParams(window.location.search)
    const oauthToken = urlParams.get('oauth_token')
    if (oauthToken) {
      localStorage.setItem('token', oauthToken)
      // Obtener perfil del agente y redirigir limpio
      fetch('/api/auth/profile', {
        headers: { Authorization: `Bearer ${oauthToken}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.agent) {
            localStorage.setItem('agent', JSON.stringify(data.agent))
            setAgent(data.agent)
            setIsAuthenticated(true)
          }
          // Limpiar oauth_token de la URL sin recargar
          window.history.replaceState({}, '', window.location.pathname)
          setIsLoading(false)
        })
        .catch(() => {
          localStorage.removeItem('token')
          setIsLoading(false)
        })
      return
    }

    const token = localStorage.getItem('token')
    const agentData = localStorage.getItem('agent')

    if (token && agentData) {
      try {
        const parsedAgent = JSON.parse(agentData)
        setAgent(parsedAgent)
        setIsAuthenticated(true)

        // Verificar token y obtener datos actualizados (incluyendo license)
        authService.getProfile()
          .then((response) => {
            if (response.agent) {
              setAgent(response.agent)
              localStorage.setItem('agent', JSON.stringify(response.agent))
            }
          })
          .catch(() => {
            localStorage.removeItem('token')
            localStorage.removeItem('agent')
            setAgent(null)
            setIsAuthenticated(false)
          })
      } catch (error) {
        localStorage.removeItem('token')
        localStorage.removeItem('agent')
      }
    }

    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password })
    localStorage.setItem('token', response.token)
    localStorage.setItem('agent', JSON.stringify(response.agent))
    setAgent(response.agent)
    setIsAuthenticated(true)
    return response
  }

  const register = async (data: any) => {
    const response = await authService.register(data)
    localStorage.setItem('token', response.token)
    localStorage.setItem('agent', JSON.stringify(response.agent))
    setAgent(response.agent)
    setIsAuthenticated(true)
    return response
  }

  const updateAgent = (updatedAgent: Agent) => {
    setAgent(updatedAgent)
    localStorage.setItem('agent', JSON.stringify(updatedAgent))
  }

  const refreshAgent = async () => {
    try {
      const response = await authService.getProfile()
      if (response.agent) {
        setAgent(response.agent)
        localStorage.setItem('agent', JSON.stringify(response.agent))
        return response.agent
      }
    } catch (error) {
      console.error('Error refreshing agent:', error)
    }
    return null
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('agent')
    setAgent(null)
    setIsAuthenticated(false)
    window.location.href = '/'
  }

  return {
    agent,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateAgent,
    refreshAgent
  }
}
